import { makeAutoObservable } from "mobx";

// eslint-disable-next-line import/no-unused-modules -- public type of the loop-source preview store API
export type LoopSourcePreviewTarget =
  | { kind: "trigger" }
  | { kind: "action"; actionHash: string };

/** UI-only state for the React Flow canvas. Lives outside components because
 *  RF nodes can remount (virtualization, graph rebuilds) and would lose
 *  local state. */
export class FlowCanvasStore {
  collapsedLoopIds = new Set<number>();

  /** Measured header heights per loop instance. A loop header is absolutely
   *  positioned, so it never grows its own node box; the layout engine reads
   *  these to reserve the real header height for a wrapped, multi-line group
   *  name (BUG-21207). Keyed by loop `instanceId`. */
  loopHeaderHeights = new Map<number, number>();

  viewportResetKey = 0;

  /** Bumped when the canvas pane is clicked so open card menus close.
   *  FlowCanvas node wrappers subscribe via `useFlowCanvasMenuDismiss`. */
  menuDismissSignal = 0;

  /** Temporary hover preview from the Looping source dropdown. */
  loopSourcePreviewTarget: LoopSourcePreviewTarget | undefined = undefined;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  isLoopCollapsed(instanceId: number): boolean {
    return this.collapsedLoopIds.has(instanceId);
  }

  toggleLoopCollapsed(instanceId: number): void {
    if (this.collapsedLoopIds.has(instanceId)) {
      this.collapsedLoopIds.delete(instanceId);
    } else {
      this.collapsedLoopIds.add(instanceId);
    }
  }

  setLoopHeaderHeight(instanceId: number, height: number): void {
    // Skip no-op writes so the ResizeObserver callback doesn't churn the
    // observable (and the layout memo) on every idempotent measure.
    if (this.loopHeaderHeights.get(instanceId) === height) return;
    this.loopHeaderHeights.set(instanceId, height);
  }

  dismissOpenMenus(): void {
    this.menuDismissSignal += 1;
  }

  setLoopSourcePreviewTarget(
    target: LoopSourcePreviewTarget | undefined,
  ): void {
    this.loopSourcePreviewTarget = target;
  }

  reset(): void {
    this.collapsedLoopIds.clear();
    this.loopHeaderHeights.clear();
    this.viewportResetKey += 1;
    this.loopSourcePreviewTarget = undefined;
  }
}
